import React, { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import type { Transition } from 'history';
import confirm from 'modules/confirm';
import { useBlocker } from 'hooks/useBlocker';

type UnsavedChangesGuardProps = {
  className?: string;
  when: boolean;
  title: string;
  content: string;
  submitBtnText: string;
  cancelBtnText: string;
};

// Asks before leaving the page; staying on it (mobile tabs) is let through.
// History's `block` also brings the browser's own prompt on reload or tab close.
const UnsavedChangesGuard: React.FC<UnsavedChangesGuardProps> = (props) => {
  const { className, when, title, content, submitBtnText, cancelBtnText } =
    props;

  const location = useLocation();

  // Letting a tab switch through drops the block; a new `key` per navigation re-arms it.
  const blocker = useCallback(
    async (tx: Transition) => {
      if (tx.location.pathname === location.pathname) return tx.retry();

      const shouldLeave = await confirm({
        className,
        title,
        content,
        submitBtnText,
        cancelBtnText,
      });

      if (shouldLeave) tx.retry();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      location.key,
      location.pathname,
      className,
      title,
      content,
      submitBtnText,
      cancelBtnText,
    ],
  );

  useBlocker(blocker, when);

  return null;
};

export default UnsavedChangesGuard;
